import * as i0 from '@angular/core';
import { input, effect, Directive, computed, booleanAttribute, afterRenderEffect, signal, contentChild, inject, ElementRef, HostListener } from '@angular/core';
import { uniqueId, onBooleanChange, controlStatus, onChange } from 'ng-primitives/utils';
import { createStateToken, createStateProvider, createStateInjector, createState } from 'ng-primitives/state';
import { injectElementRef, explicitEffect } from 'ng-primitives/internal';
import { NgControl } from '@angular/forms';

/**
 * The state token  for the FormField primitive.
 */
const NgpFormFieldStateToken = createStateToken('FormField');
/**
 * Provides the FormField state.
 */
const provideFormFieldState = createStateProvider(NgpFormFieldStateToken);
/**
 * Injects the FormField state.
 */
const injectFormFieldState = createStateInjector(NgpFormFieldStateToken);
/**
 * The FormField state registration function.
 */
const formFieldState = createState(NgpFormFieldStateToken);

/**
 * The `NgpDescription` directive is used to mark a description element within a form field. There may be multiple descriptions associated with a form control.
 */
let NgpDescription = /*#__PURE__*/(() => {
  class NgpDescription {
    constructor() {
      /**
       * The id of the description. If not provided, a unique id will be generated.
       */
      this.id = input(uniqueId('ngp-description'));
      /**
       * Access the form field that the description is associated with.
       */
      this.formField = injectFormFieldState({
        optional: true
      });
      effect(onCleanup => {
        this.formField()?.addDescription(this.id());
        onCleanup(() => this.formField()?.removeDescription(this.id()));
      });
    }
    static {
      this.ɵfac = function NgpDescription_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgpDescription)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NgpDescription,
        selectors: [["", "ngpDescription", ""]],
        hostVars: 8,
        hostBindings: function NgpDescription_HostBindings(rf, ctx) {
          if (rf & 2) {
            let tmp_1_0;
            let tmp_2_0;
            let tmp_3_0;
            let tmp_4_0;
            let tmp_5_0;
            let tmp_6_0;
            let tmp_7_0;
            i0.ɵɵattribute("id", ctx.id())("data-invalid", ((tmp_1_0 = ctx.formField()) == null ? null : tmp_1_0.invalid()) ? "" : null)("data-valid", ((tmp_2_0 = ctx.formField()) == null ? null : tmp_2_0.valid()) ? "" : null)("data-touched", ((tmp_3_0 = ctx.formField()) == null ? null : tmp_3_0.touched()) ? "" : null)("data-pristine", ((tmp_4_0 = ctx.formField()) == null ? null : tmp_4_0.pristine()) ? "" : null)("data-dirty", ((tmp_5_0 = ctx.formField()) == null ? null : tmp_5_0.dirty()) ? "" : null)("data-pending", ((tmp_6_0 = ctx.formField()) == null ? null : tmp_6_0.pending()) ? "" : null)("data-disabled", ((tmp_7_0 = ctx.formField()) == null ? null : tmp_7_0.disabled()) ? "" : null);
          }
        },
        inputs: {
          id: [1, "id"]
        },
        exportAs: ["ngpDescription"]
      });
    }
  }
  return NgpDescription;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * The `NgpError` directive is used to mark an error message element within a form field. There may be multiple error messages associated with a form control.
 */
let NgpError = /*#__PURE__*/(() => {
  class NgpError {
    constructor() {
      /**
       * Access the form field that the description is associated with.
       */
      this.formField = injectFormFieldState({
        optional: true
      });
      /**
       * The id of the error message. If not provided, a unique id will be generated.
       */
      this.id = input(uniqueId('ngp-error'));
      /**
       * The validator associated with the error message.
       */
      this.validator = input(null, {
        alias: 'ngpErrorValidator'
      });
      /**
       * Determine if there is an error message.
       */
      this.hasError = computed(() => {
        const errors = this.formField()?.errors() ?? [];
        const validator = this.validator();
        return validator ? errors?.includes(validator) : errors?.length > 0;
      });
      /**
       * Determine whether the validator associated with this error is failing.
       */
      this.state = computed(() => this.hasError() ? 'fail' : 'pass');
      // add or remove the error message when the error state changes
      onBooleanChange(this.hasError, () => this.formField()?.addDescription(this.id()), () => this.formField()?.removeDescription(this.id()));
    }
    ngOnChanges(changes) {
      if ('id' in changes) {
        this.formField()?.removeDescription(changes['id'].previousValue);
      }
    }
    ngOnDestroy() {
      this.formField()?.removeDescription(this.id());
    }
    static {
      this.ɵfac = function NgpError_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgpError)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NgpError,
        selectors: [["", "ngpError", ""]],
        hostVars: 9,
        hostBindings: function NgpError_HostBindings(rf, ctx) {
          if (rf & 2) {
            let tmp_1_0;
            let tmp_2_0;
            let tmp_3_0;
            let tmp_4_0;
            let tmp_5_0;
            let tmp_6_0;
            let tmp_7_0;
            i0.ɵɵattribute("id", ctx.id())("data-invalid", ((tmp_1_0 = ctx.formField()) == null ? null : tmp_1_0.invalid()) ? "" : null)("data-valid", ((tmp_2_0 = ctx.formField()) == null ? null : tmp_2_0.valid()) ? "" : null)("data-touched", ((tmp_3_0 = ctx.formField()) == null ? null : tmp_3_0.touched()) ? "" : null)("data-pristine", ((tmp_4_0 = ctx.formField()) == null ? null : tmp_4_0.pristine()) ? "" : null)("data-dirty", ((tmp_5_0 = ctx.formField()) == null ? null : tmp_5_0.dirty()) ? "" : null)("data-pending", ((tmp_6_0 = ctx.formField()) == null ? null : tmp_6_0.pending()) ? "" : null)("data-disabled", ((tmp_7_0 = ctx.formField()) == null ? null : tmp_7_0.disabled()) ? "" : null)("data-validator", ctx.state());
          }
        },
        inputs: {
          id: [1, "id"],
          validator: [1, "ngpErrorValidator", "validator"]
        },
        exportAs: ["ngpError"],
        features: [i0.ɵɵNgOnChangesFeature]
      });
    }
  }
  return NgpError;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * The state token  for the FormControl primitive.
 */
const NgpFormControlStateToken = createStateToken('FormControl');
/**
 * Provides the FormControl state.
 */
const provideFormControlState = createStateProvider(NgpFormControlStateToken);
/**
 * Injects the FormControl state.
 */
const injectFormControlState = createStateInjector(NgpFormControlStateToken);
/**
 * The FormControl state registration function.
 */
const formControlState = createState(NgpFormControlStateToken);

/**
 * Typically this primitive would be not be used directly, but instead a more specific form control primitive would be used (e.g. `ngpInput`). All of our form control primitives use `ngpFormControl` internally so they will have the same accessibility features as described below.
 *
 * The `NgpFormControl` directive is used to mark a form control element within a form field. This element will have an `aria-labelledby` attribute set to the ID of the label element within the form field and an `aria-describedby` attribute set to the ID of the description elements within the form field.
 */
let NgpFormControl = /*#__PURE__*/(() => {
  class NgpFormControl {
    constructor() {
      /**
       * The id of the form control. If not provided, a unique id will be generated.
       */
      this.id = input(uniqueId('ngp-form-control'));
      /**
       * Whether the form control is disabled by a parent.
       */
      this.disabled = input(false, {
        alias: 'ngpFormControlDisabled',
        transform: booleanAttribute
      });
      /**
       * The state of the form control.
       */
      this.state = formControlState(this);
      // Sync the form control state with the control state.
      setupFormControl({
        id: this.state.id,
        disabled: this.state.disabled
      });
    }
    static {
      this.ɵfac = function NgpFormControl_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgpFormControl)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NgpFormControl,
        selectors: [["", "ngpFormControl", ""]],
        inputs: {
          id: [1, "id"],
          disabled: [1, "ngpFormControlDisabled", "disabled"]
        },
        exportAs: ["ngpFormControl"],
        features: [i0.ɵɵProvidersFeature([provideFormControlState()])]
      });
    }
  }
  return NgpFormControl;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function setupFormControl({
  id,
  disabled = signal(false)
}) {
  const element = injectElementRef().nativeElement;
  // Access the form field that the form control is associated with.
  const formField = injectFormFieldState({
    optional: true
  });
  // Access the form control status.
  const status = controlStatus();
  // Determine the aria-labelledby attribute value.
  const ariaLabelledBy = computed(() => formField()?.labels().join(' '));
  // Determine the aria-describedby attribute value.
  const ariaDescribedBy = computed(() => formField()?.descriptions().join(' '));
  explicitEffect([id], ([id], onCleanup) => {
    formField()?.setFormControl(id);
    onCleanup(() => formField()?.removeFormControl());
  });
  afterRenderEffect({
    write: () => {
      setAttribute(element, 'id', id());
      setAttribute(element, 'aria-labelledby', ariaLabelledBy());
      setAttribute(element, 'aria-describedby', ariaDescribedBy());
      setStateAttribute(element, status().invalid, 'data-invalid');
      setStateAttribute(element, status().valid, 'data-valid');
      setStateAttribute(element, status().touched, 'data-touched');
      setStateAttribute(element, status().pristine, 'data-pristine');
      setStateAttribute(element, status().dirty, 'data-dirty');
      setStateAttribute(element, status().pending, 'data-pending');
      setStateAttribute(element, disabled() || status().disabled, 'data-disabled');
    }
  });
  return computed(() => ({
    ...status(),
    disabled: status().disabled || disabled()
  }));
}
/**
 * Sets the attribute on the element. If the value is not empty, the attribute is set to the value.
 * If the value is empty, the attribute is removed.
 * @param element The element to set the attribute on.
 * @param attribute The attribute to set on the element.
 * @param value The value to set on the attribute.
 */
function setAttribute(element, attribute, value) {
  if (value && value.length > 0) {
    element.setAttribute(attribute, value);
  } else {
    element.removeAttribute(attribute);
  }
}
/**
 * Sets the attribute on the element based on the state. If the state is true, the attribute
 * is set to an empty string. If the state is false, the attribute is removed.
 * @param element The element to set the attribute on.
 * @param state The state to set the attribute based on.
 * @param attribute The attribute to set on the element.
 */
function setStateAttribute(element, state, attribute) {
  if (state) {
    element.setAttribute(attribute, '');
  } else {
    element.removeAttribute(attribute);
  }
}

/**
 * The `NgpFormField` directive is a container for form field elements. Any labels, form controls, or descriptions should be placed within this directive.
 */
let NgpFormField = /*#__PURE__*/(() => {
  class NgpFormField {
    constructor() {
      /**
       * Store the form label.
       * @internal
       */
      this.labels = signal([]);
      /**
       * Store the form descriptions.
       * @internal
       */
      this.descriptions = signal([]);
      /**
       * Store the id of the associated form control.
       * @internal
       */
      this.formControl = signal(null);
      /**
       * Find any NgControl within the form field.
       * @internal
       */
      this.ngControl = contentChild(NgControl);
      /**
       * Store the validation error messages.
       * @internal
       */
      this.errors = signal([]);
      /**
       * Whether the control is pristine.
       * @internal
       */
      this.pristine = signal(null);
      /**
       * Whether the control is touched.
       * @internal
       */
      this.touched = signal(null);
      /**
       * Whether the control is dirty.
       * @internal
       */
      this.dirty = signal(null);
      /**
       * Whether the control is valid.
       */
      this.valid = signal(null);
      /**
       * Whether the control is invalid.
       * @internal
       */
      this.invalid = signal(null);
      /**
       * Whether the control is pending.
       * @internal
       */
      this.pending = signal(null);
      /**
       * Whether the control is disabled.
       * @internal
       */
      this.disabled = signal(null);
      /**
       * The form field state.
       */
      this.state = formFieldState(this);
      // any time the ngControl changes, setup the subscriptions.
      onChange(this.ngControl, this.setupSubscriptions.bind(this));
    }
    ngOnDestroy() {
      this.subscription?.unsubscribe();
    }
    /**
     * Setup a listener for the form control status.
     * @param control
     */
    setupSubscriptions(control) {
      // Unsubscribe from the previous subscriptions.
      this.subscription?.unsubscribe();
      // set the initial values
      this.updateStatus();
      const underlyingControl = control?.control;
      // Listen for changes to the underlying control's status.
      this.subscription = underlyingControl?.events?.subscribe(this.updateStatus.bind(this));
    }
    updateStatus() {
      const control = this.ngControl();
      if (!control) {
        return;
      }
      this.pristine.set(control.pristine);
      this.touched.set(control.touched);
      this.dirty.set(control.dirty);
      this.valid.set(control.valid);
      this.invalid.set(control.invalid);
      this.pending.set(control.pending);
      this.disabled.set(control.disabled);
      this.errors.set(control?.errors ? Object.keys(control.errors) : []);
    }
    /**
     * Register the id of the associated form control.
     * @param id
     * @internal
     */
    setFormControl(id) {
      this.formControl.set(id);
    }
    /**
     * Register a label with the form field.
     * @param label
     * @internal
     */
    addLabel(label) {
      this.labels.update(labels => [...labels, label]);
    }
    /**
     * Register a description with the form field.
     * @param description
     * @internal
     */
    addDescription(description) {
      this.descriptions.update(descriptions => [...descriptions, description]);
    }
    /**
     * Remove the associated form control.
     * @internal
     */
    removeFormControl() {
      this.formControl.set(null);
    }
    /**
     * Remove a label from the form field.
     * @param label
     * @internal
     */
    removeLabel(label) {
      this.labels.update(labels => labels.filter(l => l !== label));
    }
    /**
     * Remove a description from the form field.
     * @param description
     * @internal
     */
    removeDescription(description) {
      this.descriptions.update(descriptions => descriptions.filter(d => d !== description));
    }
    static {
      this.ɵfac = function NgpFormField_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgpFormField)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NgpFormField,
        selectors: [["", "ngpFormField", ""]],
        contentQueries: function NgpFormField_ContentQueries(rf, ctx, dirIndex) {
          if (rf & 1) {
            i0.ɵɵcontentQuerySignal(dirIndex, ctx.ngControl, NgControl, 5);
          }
          if (rf & 2) {
            i0.ɵɵqueryAdvance();
          }
        },
        hostVars: 7,
        hostBindings: function NgpFormField_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵattribute("data-invalid", ctx.invalid() ? "" : null)("data-valid", ctx.valid() ? "" : null)("data-touched", ctx.touched() ? "" : null)("data-pristine", ctx.pristine() ? "" : null)("data-dirty", ctx.dirty() ? "" : null)("data-pending", ctx.pending() ? "" : null)("data-disabled", ctx.disabled() ? "" : null);
          }
        },
        exportAs: ["ngpFormField"],
        features: [i0.ɵɵProvidersFeature([provideFormFieldState()])]
      });
    }
  }
  return NgpFormField;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * The `NgpLabel` directive is used to mark a label element within a form field. Preferably, there should use an HTML `<label>` element.
 */
let NgpLabel = /*#__PURE__*/(() => {
  class NgpLabel {
    constructor() {
      /**
       * The id of the label. If not provided, a unique id will be generated.
       */
      this.id = input(uniqueId('ngp-label'));
      /**
       * Access the form field that the label is associated with.
       */
      this.formField = injectFormFieldState({
        optional: true
      });
      /**
       * Derive the for attribute value if the label is an HTML label element.
       */
      this.htmlFor = computed(() => this.formField()?.formControl());
      /**
       * Access the element that the label is associated with.
       */
      this.elementRef = inject(ElementRef);
      /**
       * Determine if the label is an HTML label element.
       */
      this.isLabel = this.elementRef.nativeElement instanceof HTMLLabelElement;
      effect(onCleanup => {
        this.formField()?.addLabel(this.id());
        onCleanup(() => this.formField()?.removeLabel(this.id()));
      });
    }
    onClick(event) {
      // by default a label will perform a click on the associated form control, however
      // this only works if the associated form control is an input element which may not always
      // be the case, so we prevent the default behavior and handle the click event ourselves.
      // This was inspired by the HeadlessUI approach:
      // https://github.com/tailwindlabs/headlessui/blob/main/packages/%40headlessui-react/src/components/label/label.tsx#L58
      if (this.isLabel) {
        event.preventDefault();
      }
      // to find the associated form control we can lookup via the known id
      const targetId = this.htmlFor();
      if (!targetId) {
        return;
      }
      const target = document.getElementById(targetId);
      if (!target) {
        return;
      }
      // if the target is disabled then do nothing
      const disabled = target.getAttribute('disabled');
      const ariaDisabled = target.getAttribute('aria-disabled');
      if (disabled === '' || disabled === 'true' || ariaDisabled === 'true') {
        return;
      }
      // radio buttons, checkboxes and switches should all be clicked immediately as they require state changes
      if (target instanceof HTMLInputElement && (target.type === 'radio' || target.type === 'checkbox') || target.role === 'radio' || target.role === 'checkbox' || target.role === 'switch') {
        target.click();
      }
      // Move focus to the element, this allows you to start using keyboard shortcuts since the
      // bound element is now focused.
      target.focus({
        preventScroll: true
      });
    }
    static {
      this.ɵfac = function NgpLabel_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgpLabel)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NgpLabel,
        selectors: [["", "ngpLabel", ""]],
        hostVars: 9,
        hostBindings: function NgpLabel_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("click", function NgpLabel_click_HostBindingHandler($event) {
              return ctx.onClick($event);
            });
          }
          if (rf & 2) {
            let tmp_2_0;
            let tmp_3_0;
            let tmp_4_0;
            let tmp_5_0;
            let tmp_6_0;
            let tmp_7_0;
            let tmp_8_0;
            i0.ɵɵattribute("id", ctx.id())("for", ctx.htmlFor())("data-invalid", ((tmp_2_0 = ctx.formField()) == null ? null : tmp_2_0.invalid()) ? "" : null)("data-valid", ((tmp_3_0 = ctx.formField()) == null ? null : tmp_3_0.valid()) ? "" : null)("data-touched", ((tmp_4_0 = ctx.formField()) == null ? null : tmp_4_0.touched()) ? "" : null)("data-pristine", ((tmp_5_0 = ctx.formField()) == null ? null : tmp_5_0.pristine()) ? "" : null)("data-dirty", ((tmp_6_0 = ctx.formField()) == null ? null : tmp_6_0.dirty()) ? "" : null)("data-pending", ((tmp_7_0 = ctx.formField()) == null ? null : tmp_7_0.pending()) ? "" : null)("data-disabled", ((tmp_8_0 = ctx.formField()) == null ? null : tmp_8_0.disabled()) ? "" : null);
          }
        },
        inputs: {
          id: [1, "id"]
        },
        exportAs: ["ngpLabel"]
      });
    }
  }
  return NgpLabel;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NgpDescription, NgpError, NgpFormControl, NgpFormField, NgpLabel, injectFormControlState, injectFormFieldState, provideFormControlState, provideFormFieldState, setupFormControl };
